/**
 * AI 가드레일 창 — 안내(가드레일 5) · 정책(사용자별 규칙, 가드레일 1) · 치환 장부.
 * docs/features/21-ai-guardrail.md
 *
 * `mode='ack'` 이면 처음 사용 전 확인 — 안내를 보여 주고 「확인했습니다」 로 닫는다. 확인은 **사람이 누른 것만**이다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiShield, FiPlus, FiTrash2, FiRotateCcw } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/guardrail';
// 안내는 사례·리포트 문서와 같은 문서 서식(.stn-md)
import '../sqlTuning/ReportView.css';
import './Guardrail.css';

const TABS = ['guide', 'rules', 'tokens'];
const KINDS = ['builtin', 'keyword', 'regex'];
/**
 * 안내는 두 갈래(2026-10-26 사용자 요청) — 가. 외부 LLM 으로 보내는 내용 · 나. 실행 범위. 갈래마다 원칙 · 방안 · 예외.
 * 모양은 사례·리포트 문서와 같은 문서 서식(.stn-md)이다 — 이 창만 글이 달라 보였다(신고).
 */
const GUIDE = [
  { key: 'send', measures: ['exclude', 'mask', 'ask', 'review', 'export'], exceptions: ['e1', 'e2', 'e3', 'e4', 'e5'], detect: true },
  { key: 'exec', measures: ['query', 'dml', 'ddl', 'stats', 'temp', 'session'], exceptions: ['e1', 'e2', 'e3', 'e4'] },
];
/** 내장 탐지 항목 — 구분은 근거가 속한 갈래(고유식별정보·금융 …). 순서는 표에 보이는 순서 */
const DETECT_ROWS = [
  ['password', 'auth'], ['rrn', 'unique'], ['passport', 'unique'], ['license', 'unique'],
  ['card', 'credit'], ['account', 'credit'], ['email', 'contact'], ['phone', 'contact'], ['ipv4', 'network'],
];

function GuidePart({ label, tone, children }) {
  return (
    <div className="grd-part">
      <span className={`grd-chip grd-chip-${tone}`}>{label}</span>
      <div className="grd-part-body">{children}</div>
    </div>
  );
}
GuidePart.propTypes = { label: PropTypes.string.isRequired, tone: PropTypes.string.isRequired, children: PropTypes.node };

/** 안내 문서 — 원칙(방향) · 방안(어떻게 지키나) · 예외(그 밖·한계) */
function GuideDoc({ defaults }) {
  const { t } = useTranslation();
  const L = (k) => t(`common:grd.doc.labels.${k}`);
  return (
    <div className="stn-md grd-doc">
      <p className="grd-doc-intro">{t('common:grd.doc.intro')}</p>
      {GUIDE.map(sec => (
        <section key={sec.key}>
          <h2>{t(`common:grd.doc.${sec.key}.title`)}</h2>
          <GuidePart label={L('principle')} tone="p"><p>{t(`common:grd.doc.${sec.key}.principle`)}</p></GuidePart>
          <GuidePart label={L('measure')} tone="m">
            <ul>
              {sec.measures.map(m => (
                <li key={m}><strong>{t(`common:grd.doc.${sec.key}.m.${m}.name`)}</strong> — {t(`common:grd.doc.${sec.key}.m.${m}.body`)}</li>
              ))}
            </ul>
            {sec.detect && (
              <>
                <h3>{t('common:grd.doc.detect.title')}</h3>
                <table className="grd-detect">
                  <thead>
                    <tr>
                      <th>{t('common:grd.doc.detect.cols.item')}</th>
                      <th>{t('common:grd.doc.detect.cols.cat')}</th>
                      <th>{t('common:grd.doc.detect.cols.basis')}</th>
                      <th>{t('common:grd.doc.detect.cols.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DETECT_ROWS.map(([b, cat]) => (
                      <tr key={b}>
                        <td>{t(`common:grd.builtins.${b}`)}</td>
                        <td>{t(`common:grd.cat.${cat}`)}</td>
                        <td>{t(`common:grd.basis.${b}`)}</td>
                        <td>{defaults && defaults[b] ? t(`common:grd.actions.${defaults[b]}`) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="grd-note">{t('common:grd.doc.detect.note')}</p>
              </>
            )}
          </GuidePart>
          <GuidePart label={L('exception')} tone="x">
            <ul>{sec.exceptions.map(x => <li key={x}>{t(`common:grd.doc.${sec.key}.x.${x}`)}</li>)}</ul>
          </GuidePart>
        </section>
      ))}
      <p className="grd-note grd-doc-foot">{t('common:grd.doc.footer')}</p>
    </div>
  );
}
GuideDoc.propTypes = { defaults: PropTypes.object };

const newRule = (n) => ({ id: `r${Date.now().toString(36)}${n}`, name: '', kind: 'keyword', words: [], action: 'mask', enabled: true });
const NAME_RULE_ID = 'tbl';     // 기본 규칙 「테이블·스키마 이름」(guardrail/store.js DEFAULT_RULES)

/**
 * 연결에서 이름 가져오기 — 고른 연결의 스키마·테이블·뷰 이름을 후보로 보여 주고, 고른 것만 「테이블·스키마 이름」 규칙에 넣는다.
 * 정책은 여기서 저장하지 않는다(사람이 「저장」 해야 적용).
 */
function NameSuggest({ connections, onAdd }) {
  const { t } = useTranslation();
  const usable = (connections || []).filter(c => ['oracle', 'postgresql'].includes(c.dbType || 'oracle'));
  const [connId, setConnId] = useState('');
  const [names, setNames] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  if (!usable.length) return null;
  const load = async () => {
    setBusy(true);
    try {
      const r = await api.suggestNames(connId);
      setNames(r.names || []);
      setPicked(new Set(r.names || []));
      if (r.truncated) toast.info(t('common:grd.suggest.truncated', { max: r.max }));
    } catch (e) { toast.error(formatApiError(e, t)); } finally { setBusy(false); }
  };
  const toggle = (n) => setPicked((p) => { const x = new Set(p); if (x.has(n)) x.delete(n); else x.add(n); return x; });
  return (
    <div className="grd-suggest">
      <span className="grd-label">{t('common:grd.suggest.title')}</span>
      <span className="grd-note">{t('common:grd.suggest.hint')}</span>
      <div className="grd-row">
        <select value={connId} onChange={e => { setConnId(e.target.value); setNames(null); }} aria-label={t('common:grd.suggest.pick')}>
          <option value="">{t('common:grd.suggest.pick')}</option>
          {usable.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
        </select>
        <button type="button" className="app-btn" onClick={load} disabled={!connId || busy}>{t('common:grd.suggest.load')}</button>
      </div>
      {names && !names.length && <span className="grd-note">{t('common:grd.suggest.empty')}</span>}
      {names && names.length > 0 && (
        <>
          <div className="grd-row">
            <button type="button" className="app-btn" onClick={() => setPicked(new Set(names))}>{t('common:grd.suggest.all')}</button>
            <button type="button" className="app-btn" onClick={() => setPicked(new Set())}>{t('common:grd.suggest.none')}</button>
            <span className="grd-grow" />
            <button type="button" className="app-btn app-btn-primary" disabled={!picked.size}
              onClick={() => { onAdd([...picked]); setNames(null); }}>{t('common:grd.suggest.add', { count: picked.size })}</button>
          </div>
          <div className="grd-names">
            {names.map(n => (
              <label key={n} className="mono"><input type="checkbox" checked={picked.has(n)} onChange={() => toggle(n)} /> {n}</label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

NameSuggest.propTypes = { connections: PropTypes.arrayOf(PropTypes.object), onAdd: PropTypes.func.isRequired };

function RuleRow({ rule, builtins, actions, onChange, onRemove }) {
  const { t } = useTranslation();
  const set = (patch) => onChange({ ...rule, ...patch });
  return (
    <tr className={rule.enabled === false ? 'grd-off' : ''}>
      <td><input type="checkbox" checked={rule.enabled !== false} onChange={e => set({ enabled: e.target.checked })} aria-label={t('common:grd.enabled')} /></td>
      <td><input value={rule.name} onChange={e => set({ name: e.target.value })} placeholder={t('common:grd.ruleName')} /></td>
      <td>
        <select value={rule.kind} onChange={e => set({ kind: e.target.value })}>
          {KINDS.map(k => <option key={k} value={k}>{t(`common:grd.kinds.${k}`)}</option>)}
        </select>
      </td>
      <td className="grd-target">
        {rule.kind === 'builtin' && (
          <select value={rule.builtin || ''} onChange={e => set({ builtin: e.target.value })}>
            <option value="" disabled>{t('common:grd.pickBuiltin')}</option>
            {builtins.map(b => <option key={b} value={b}>{t(`common:grd.builtins.${b}`, { defaultValue: b })}</option>)}
          </select>
        )}
        {rule.kind === 'keyword' && (
          <textarea rows={2} value={(rule.words || []).join('\n')} onChange={e => set({ words: e.target.value.split('\n') })}
            placeholder={t('common:grd.wordsHint')} />
        )}
        {rule.kind === 'regex' && (
          <input className="mono" value={rule.pattern || ''} onChange={e => set({ pattern: e.target.value })} placeholder={t('common:grd.regexHint')} />
        )}
      </td>
      <td>
        <select value={rule.action} onChange={e => set({ action: e.target.value })}>
          {actions.map(a => <option key={a} value={a}>{t(`common:grd.actions.${a}`)}</option>)}
        </select>
      </td>
      {/* 내장 탐지기만 근거가 있다 — 국내 개인정보 지침의 어느 항목인지(2026-10-26) */}
      <td className="grd-basis">{rule.kind === 'builtin' && rule.builtin ? t(`common:grd.basis.${rule.builtin}`, { defaultValue: '' }) : ''}</td>
      <td><button type="button" className="app-btn" onClick={onRemove} title={t('common:ui.delete')}><FiTrash2 size={12} /></button></td>
    </tr>
  );
}

export default function GuardrailModal({ open, mode = 'view', onClose, onAcknowledged, connections }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [tab, setTab] = useState('guide');
  const [policy, setPolicy] = useState(null);
  const [rules, setRules] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState('');
  const [scan, setScan] = useState(null);
  const [tokens, setTokens] = useState(null);

  const load = useCallback(async () => {
    try {
      const p = await api.getPolicy();
      setPolicy(p); setRules(p.rules || []); setDirty(false);
    } catch (e) { toast.error(formatApiError(e, t)); }
  }, [t]);

  // 열 때 한 번만 — load 는 t 에 묶여 있어 의존성에 넣으면 언어가 바뀌거나 t 가 새로 올 때 탭이 처음으로 돌아간다
  useEffect(() => { if (open) { setTab('guide'); setScan(null); setTokens(null); load(); } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]);
  useEffect(() => {
    if (open && tab === 'tokens' && !tokens) api.listTokens().then(r => setTokens(r.tokens || [])).catch(e => toast.error(formatApiError(e, t)));
  }, [open, tab, tokens, t]);

  const edit = (i, r) => { setRules(rs => rs.map((x, j) => (j === i ? r : x))); setDirty(true); };
  const remove = (i) => { setRules(rs => rs.filter((_, j) => j !== i)); setDirty(true); };
  const add = () => { setRules(rs => [...rs, newRule(rs.length)]); setDirty(true); };
  // 가져온 이름을 「테이블·스키마 이름」 낱말 규칙에 합친다(없으면 만든다). 저장은 사람이
  const addNames = (list) => {
    setRules((rs) => {
      const i = rs.findIndex(r => r.id === NAME_RULE_ID && r.kind === 'keyword');
      if (i < 0) {
        toast.info(t('common:grd.suggest.noRule'));
        return [...rs, { id: NAME_RULE_ID, name: t('common:grd.suggest.title'), kind: 'keyword', words: list, action: 'mask', enabled: true }];
      }
      const words = [...new Set([...(rs[i].words || []), ...list])];
      return rs.map((r, j) => (j === i ? { ...r, words } : r));
    });
    setDirty(true);
    toast.success(t('common:grd.suggest.added', { count: list.length }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = rules.map(r => ({ ...r, name: r.name || t('common:grd.unnamed'), words: (r.words || []).map(w => w.trim()).filter(Boolean) }));
      const p = await api.savePolicy(clean);
      setPolicy(p); setRules(p.rules); setDirty(false);
      toast.success(t('common:grd.saved'));
    } catch (e) { toast.error(formatApiError(e, t)); } finally { setBusy(false); }
  };

  const reset = async () => {
    const ok = await confirm({ title: t('common:grd.resetTitle'), message: t('common:grd.resetBody'), confirmText: t('common:grd.reset'), variant: 'danger' });
    if (!ok) return;
    try { const p = await api.resetPolicy(); setPolicy(p); setRules(p.rules); setDirty(false); } catch (e) { toast.error(formatApiError(e, t)); }
  };

  const runScan = async () => {
    try { setScan(await api.scanText(sample, rules)); } catch (e) { toast.error(formatApiError(e, t)); }
  };

  const clearAll = async () => {
    const ok = await confirm({ title: t('common:grd.clearTokensTitle'), message: t('common:grd.clearTokensBody'), confirmText: t('common:grd.clearTokens'), variant: 'danger' });
    if (!ok) return;
    try { await api.clearTokens(); setTokens([]); } catch (e) { toast.error(formatApiError(e, t)); }
  };

  const ack = async () => {
    setBusy(true);
    try {
      if (dirty) await save();
      const p = await api.acknowledge();
      setPolicy(p);
      if (onAcknowledged) onAcknowledged();
    } catch (e) { toast.error(formatApiError(e, t)); } finally { setBusy(false); }
  };

  const close = async () => {
    if (dirty) {
      const ok = await confirm({ title: t('common:grd.discardTitle'), message: t('common:grd.discardBody'), confirmText: t('common:grd.discard'), variant: 'danger' });
      if (!ok) return;
    }
    onClose();
  };

  // 저장·되돌리기의 응답에는 탐지기 목록이 없다 — 처음 받은 것을 계속 쓴다
  const meta = useRef({});
  if (policy && policy.builtins) meta.current = { builtins: policy.builtins, defaults: policy.builtinDefaults || meta.current.defaults };
  const builtins = (policy && policy.builtins) || meta.current.builtins || [];
  const builtinDefaults = (policy && policy.builtinDefaults) || meta.current.defaults || null;
  const actions = (policy && policy.actions) || ['exclude', 'mask', 'ask', 'off'];

  return (
    <AppModal overlayClassName="grd-overlay" open={open} onClose={close} title={t('common:grd.title')} icon={<FiShield size={14} />} className="app-modal-default grd-modal">
      <div className="grd-body">
        {mode === 'ack' && <div className="grd-ack-banner">{t('common:grd.ackBanner')}</div>}
        <div className="grd-ribbon" role="tablist">
          {TABS.map(k => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`grd-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
              {t(`common:grd.tabs.${k}`)}
            </button>
          ))}
        </div>

        <div className="grd-pane" key={tab}>
          {tab === 'guide' && (
            <div className="grd-guide">
              <GuideDoc defaults={builtinDefaults} />
              {policy && policy.ackAt && <p className="grd-note">{t('common:grd.ackedAt', { at: storedTime(policy.ackAt) })}</p>}
            </div>
          )}

          {tab === 'rules' && (
            <div className="grd-rules">
              <p className="grd-note">{t('common:grd.rulesHelp')}</p>
              <div className="grd-table-wrap">
                <table className="grd-table">
                  <thead>
                    <tr>
                      <th />
                      <th>{t('common:grd.cols.name')}</th>
                      <th>{t('common:grd.cols.kind')}</th>
                      <th>{t('common:grd.cols.target')}</th>
                      <th>{t('common:grd.cols.action')}</th>
                      <th>{t('common:grd.cols.basis')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r, i) => (
                      <RuleRow key={r.id} rule={r} builtins={builtins} actions={actions} onChange={x => edit(i, x)} onRemove={() => remove(i)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grd-row">
                <button type="button" className="app-btn" onClick={add}><FiPlus size={12} /> {t('common:grd.addRule')}</button>
                <button type="button" className="app-btn" onClick={reset}><FiRotateCcw size={12} /> {t('common:grd.reset')}</button>
                <span className="grd-grow" />
                <button type="button" className="app-btn app-btn-primary" onClick={save} disabled={!dirty || busy}>{t('common:action.save')}</button>
              </div>
              <NameSuggest connections={connections} onAdd={addNames} />
              <div className="grd-test">
                <span className="grd-label">{t('common:grd.testTitle')}</span>
                <textarea rows={4} value={sample} onChange={e => setSample(e.target.value)} placeholder={t('common:grd.testHint')} />
                <div className="grd-row">
                  <button type="button" className="app-btn" onClick={runScan} disabled={!sample.trim()}>{t('common:grd.test')}</button>
                  {scan && <span className="grd-note">{t('common:grd.testTotal', { count: scan.total })}</span>}
                </div>
                {scan && scan.rules.length > 0 && (
                  <ul className="grd-hits">
                    {scan.rules.map(h => (
                      <li key={h.ruleId}>
                        <b>{h.ruleName}</b> · {t(`common:grd.actions.${h.action}`)} · {t('common:grd.hitCount', { count: h.count })}
                        <span className="mono grd-previews">{h.previews.join(', ')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'tokens' && (
            <div className="grd-tokens">
              <p className="grd-note">{t('common:grd.tokensHelp')}</p>
              <div className="grd-table-wrap">
                <table className="grd-table">
                  <thead><tr><th>{t('common:grd.cols.token')}</th><th>{t('common:grd.cols.rule')}</th><th>{t('common:grd.cols.preview')}</th><th>{t('common:grd.cols.created')}</th></tr></thead>
                  <tbody>
                    {(tokens || []).map(x => (
                      <tr key={x.token}><td className="mono">{x.token}</td><td>{x.ruleId}</td><td className="mono">{x.preview == null ? t('common:grd.unreadable') : x.preview}</td><td>{storedTime(x.createdAt)}</td></tr>
                    ))}
                    {tokens && !tokens.length && <tr><td colSpan={4} className="grd-note">{t('common:grd.noTokens')}</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="grd-row">
                <span className="grd-grow" />
                <button type="button" className="app-btn app-btn-danger" onClick={clearAll} disabled={!tokens || !tokens.length}>{t('common:grd.clearTokens')}</button>
              </div>
            </div>
          )}
        </div>

        <div className="grd-foot">
          {mode === 'ack' ? (
            <>
              <button type="button" className="app-btn" onClick={close}>{t('common:grd.later')}</button>
              <button type="button" className="app-btn app-btn-primary" onClick={ack} disabled={busy}>{t('common:grd.acknowledge')}</button>
            </>
          ) : (
            <button type="button" className="app-btn" onClick={close}>{t('common:ui.close')}</button>
          )}
        </div>
      </div>
    </AppModal>
  );
}
